/**
 * `MapCamera` (03 §5, 09 §5): the pure `MapView` plus a Babylon orthographic camera locked
 * top-down. World (x, y) ↦ Babylon (x, 0, y); the eye sits at (cx, 1000, cy) looking straight
 * down with the up vector (sin rot, 0, cos rot), so screen right = (cos, −sin) and screen up =
 * (sin, cos) in world: exactly v6 `View.w2s` with screen y down.
 *
 * The viewport is the layout rect × uPxScale rounded to whole backbuffer px (origin bottom-left),
 * and the ortho bounds are derived from that rounded viewport so the GL picture and
 * `worldToStage` agree exactly.
 */
import { TargetCamera } from '@babylonjs/core/Cameras/targetCamera';
import { Camera } from '@babylonjs/core/Cameras/camera';
import { Matrix, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Viewport } from '@babylonjs/core/Maths/math.viewport';
import type { Scene } from '@babylonjs/core/scene';
import type { Rect } from '../api.ts';
import type { UniformHub } from './engine.ts';
import { LAYER } from './engine.ts';
import { normalizedViewport, orthoBounds, stageRectToBackbuffer, type NormViewport, type OrthoBounds, type PxRect } from './projection.ts';
import { MapView } from './view.ts';

/** Eye height above the ground plane (m). Layers live within a few metres of y = 0. */
export const EYE_HEIGHT = 1000;

/** A TargetCamera whose view matrix is LookAt(eye, eye − 1000·Y, upVector): no Euler angles. */
export class TopDownCamera extends TargetCamera {
  private readonly look = new Vector3();
  override _getViewMatrix(): Matrix {
    this.look.set(this.position.x, 0, this.position.z);
    if (this.getScene().useRightHandedSystem) Matrix.LookAtRHToRef(this.position, this.look, this.upVector, this._viewMatrix);
    else Matrix.LookAtLHToRef(this.position, this.look, this.upVector, this._viewMatrix);
    return this._viewMatrix;
  }
}

/** Sync a MapView-like state to a TopDownCamera (shared by MapCamera and the inset cameras). */
export class CameraSync {
  readonly px: PxRect = { x0: 0, y0: 0, x1: 0, y1: 0 };
  private readonly nv: NormViewport = { x: 0, y: 0, w: 0, h: 0 };
  private readonly ob: OrthoBounds = { left: 0, right: 0, top: 0, bottom: 0 };
  constructor(
    readonly cam: TopDownCamera,
    private readonly hub: UniformHub,
  ) {}

  apply(v: MapView, rect: Rect, pxScale: number, bbW: number, bbH: number): void {
    const px = stageRectToBackbuffer(rect, pxScale, this.px);
    const n = normalizedViewport(px, bbW, bbH, this.nv);
    const vp = this.cam.viewport;
    if (vp.x !== n.x || vp.y !== n.y || vp.width !== n.w || vp.height !== n.h) this.cam.viewport = new Viewport(n.x, n.y, n.w, n.h);
    // ortho bounds from the rounded viewport, expressed in stage px
    const b = orthoBounds(v.mpp, v.cpx, v.cpy, px.x0 / pxScale, px.y0 / pxScale, px.x1 / pxScale, px.y1 / pxScale, this.ob);
    const c = this.cam;
    c.orthoLeft = b.left;
    c.orthoRight = b.right;
    c.orthoTop = b.top;
    c.orthoBottom = b.bottom;
    c.position.set(v.cx, EYE_HEIGHT, v.cy);
    c.upVector.set(v.sin, 0, v.cos);
    this.hub.setCameraViewport(c, px.x0, bbH - px.y1, px.x1 - px.x0, px.y1 - px.y0);
  }
}

export function createTopDownCamera(name: string, scene: Scene, layerMask: number): TopDownCamera {
  const cam = new TopDownCamera(name, new Vector3(0, EYE_HEIGHT, 0), scene, false);
  cam.mode = Camera.ORTHOGRAPHIC_CAMERA;
  cam.minZ = 1;
  cam.maxZ = EYE_HEIGHT * 2;
  cam.layerMask = layerMask;
  return cam;
}

export interface MapCameraHost {
  scene: Scene;
  hub: UniformHub;
  /** backbuffer px per stage px */
  readonly pxScale: number;
  renderWidth(): number;
  renderHeight(): number;
}

/** The main map camera: implements `MapCameraApi` (via MapView) and drives a Babylon camera. */
export class MapCamera extends MapView {
  readonly cam: TopDownCamera;
  private readonly sync: CameraSync;
  private suspended = 0;

  constructor(
    private readonly host: MapCameraHost,
    rect?: Rect,
  ) {
    super(rect);
    this.cam = createTopDownCamera('map', host.scene, LAYER.MAIN);
    this.sync = new CameraSync(this.cam, host.hub);
    if (!host.scene.activeCameras?.includes(this.cam)) {
      host.scene.activeCameras = [this.cam, ...(host.scene.activeCameras ?? []).filter((c) => c !== this.cam)];
    }
    host.scene.activeCamera = this.cam;
    this.syncCamera();
  }

  /** The integer backbuffer rect of the viewport (top-left origin). */
  get viewportPx(): Readonly<PxRect> {
    return this.sync.px;
  }

  /** Re-derive viewport and ortho bounds (call after engine.applyScale). */
  syncCamera(): void {
    this.sync.apply(this, this.rect, this.host.pxScale, this.host.renderWidth(), this.host.renderHeight());
  }

  /** Batch several view mutations into one camera sync. */
  batch(fn: () => void): void {
    this.suspended++;
    try {
      fn();
    } finally {
      this.suspended--;
    }
    if (this.suspended === 0) this.changed();
  }

  protected override changed(): void {
    if (this.suspended > 0) return;
    this.syncCamera();
    super.changed();
  }

  dispose(): void {
    const sc = this.host.scene;
    if (sc.activeCameras) sc.activeCameras = sc.activeCameras.filter((c) => c !== this.cam);
    this.host.hub.removeCamera(this.cam);
    this.cam.dispose();
  }
}
